import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/redux';
import { WorkLog, WorkLogStatus } from '../types';
import { getWorkLogs, deleteWorkLog } from '../services/api/workLog';
import { PlusIcon, DocumentTextIcon, TrashIcon, PencilIcon, EyeIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import { useToast } from '../components/ToastContainer';
import WorkLogEditor from '../components/worklog/WorkLogEditor';
import { usePermissions, Permission } from '../hooks/usePermissions';

const WorkLogManagement: React.FC = () => {
  const { projectKey } = useParams<{ projectKey: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { showError, showSuccess } = useToast();
  const currentProject = useAppSelector(state => state.project.currentProject);
  const projectId = currentProject?.id;
  
  const [workLogs, setWorkLogs] = useState<WorkLog[]>([]);
  const [selectedWorkLog, setSelectedWorkLog] = useState<WorkLog | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<WorkLogStatus | 'ALL'>('ALL');

  // 載入工作日誌列表
  const loadWorkLogs = async () => {
    try {
      setIsLoading(true);
      const status = statusFilter === 'ALL' ? undefined : statusFilter;
      const data = await getWorkLogs(projectId, status);
      setWorkLogs(data);
    } catch (error) {
      console.error('Failed to load work logs:', error);
      showError('載入工作日誌失敗');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      loadWorkLogs();
    }
  }, [projectId, statusFilter]);

  // 刪除工作日誌
  const handleDelete = async (id: string) => {
    if (!hasPermission(Permission.DELETE_WORK_LOG)) {
      showError('您沒有刪除工作日誌的權限');
      return;
    }

    if (!window.confirm('確定要刪除這份工作日誌嗎？')) return;

    try {
      await deleteWorkLog(id);
      showSuccess('刪除成功');
      loadWorkLogs();
    } catch (error) {
      console.error('Failed to delete work log:', error);
      showError('刪除失敗');
    }
  };

  // 新增工作日誌
  const handleCreate = () => {
    if (!hasPermission(Permission.CREATE_WORK_LOG)) {
      showError('您沒有建立工作日誌的權限');
      return;
    }
    setSelectedWorkLog(null);
    setIsEditorOpen(true);
  };

  // 編輯工作日誌
  const handleEdit = (workLog: WorkLog) => {
    if (!hasPermission(Permission.EDIT_WORK_LOG)) {
      showError('您沒有編輯工作日誌的權限');
      return;
    }
    setSelectedWorkLog(workLog);
    setIsEditorOpen(true);
  };

  // 檢視工作日誌
  const handleView = (workLog: WorkLog) => {
    navigate(`/project/${projectKey}/worklogs/${workLog.id}`);
  };

  // 狀態顏色
  const getStatusColor = (status: WorkLogStatus) => {
    switch (status) {
      case WorkLogStatus.DRAFT:
        return 'bg-gray-100 text-gray-800';
      case WorkLogStatus.PUBLISHED:
        return 'bg-green-100 text-green-800';
      case WorkLogStatus.ARCHIVED:
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // 狀態文字
  const getStatusText = (status: WorkLogStatus) => {
    switch (status) {
      case WorkLogStatus.DRAFT:
        return '草稿';
      case WorkLogStatus.PUBLISHED:
        return '已發布';
      case WorkLogStatus.ARCHIVED:
        return '已封存';
      default:
        return status;
    }
  };

  if (!currentProject) {
    return <div className="p-8">專案不存在</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* 頁面標題 */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">工作日誌管理</h1>
            <p className="text-gray-600">{currentProject.name} - 社區管理工作日誌</p>
          </div>
          {hasPermission(Permission.CREATE_WORK_LOG) && (
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              建立工作日誌
            </button>
          )}
        </div>
      </div>

      {/* 篩選器 */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <span className="text-gray-700">狀態篩選：</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as WorkLogStatus | 'ALL')}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">全部</option>
            <option value={WorkLogStatus.DRAFT}>草稿</option>
            <option value={WorkLogStatus.PUBLISHED}>已發布</option>
            <option value={WorkLogStatus.ARCHIVED}>已封存</option>
          </select>
        </div>
      </div>

      {/* 工作日誌列表 */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : workLogs.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">尚未建立任何工作日誌</p>
          {hasPermission(Permission.CREATE_WORK_LOG) && (
            <button
              onClick={handleCreate}
              className="mt-4 text-blue-600 hover:text-blue-700"
            >
              建立第一份工作日誌
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {workLogs.map((workLog) => (
            <div
              key={workLog.id}
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className="text-xl font-semibold text-gray-900">{workLog.title}</h3>
                    <span className={`ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(workLog.status)}`}>
                      {getStatusText(workLog.status)}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>
                      期間：{format(new Date(workLog.startDate), 'yyyy年MM月dd日', { locale: zhTW })} 
                      ~ {format(new Date(workLog.endDate), 'yyyy年MM月dd日', { locale: zhTW })}
                    </div>
                    {workLog.communityName && (
                      <div>社區：{workLog.communityName}</div>
                    )}
                    {workLog.managementCompany && (
                      <div>管理公司：{workLog.managementCompany}</div>
                    )}
                    <div className="flex items-center">
                      <span>建立者：{workLog.createdBy?.name}</span>
                      <span className="mx-2">•</span>
                      <span>建立時間：{format(new Date(workLog.createdAt), 'yyyy-MM-dd HH:mm', { locale: zhTW })}</span>
                    </div>
                    {workLog.selectedIssues && workLog.selectedIssues.length > 0 && (
                      <div className="mt-2">
                        <span className="text-gray-700">關聯議題數：</span>
                        <span className="font-medium text-blue-600 ml-1">{workLog.selectedIssues.length}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleView(workLog)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="檢視"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </button>
                  {hasPermission(Permission.EDIT_WORK_LOG) && (
                    <button
                      onClick={() => handleEdit(workLog)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      title="編輯"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                  )}
                  {hasPermission(Permission.DELETE_WORK_LOG) && (
                    <button
                      onClick={() => handleDelete(workLog.id)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="刪除"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 工作日誌編輯器 */}
      {isEditorOpen && (
        <WorkLogEditor
          projectId={projectId!}
          workLog={selectedWorkLog}
          isOpen={isEditorOpen}
          onClose={() => {
            setIsEditorOpen(false);
            setSelectedWorkLog(null);
          }}
          onSave={() => {
            setIsEditorOpen(false);
            setSelectedWorkLog(null);
            loadWorkLogs();
          }}
        />
      )}
    </div>
  );
};

export default WorkLogManagement;
